import React, { useCallback, useEffect, useState } from "react";
import { Search, Mail, Phone, MessageCircle, Trash2, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg, euro, waLink } from "@/lib/api";
import { centsToEuro, LANG_NAMES } from "@/lib/pricing";
import { AdminCard, PageTitle, StatusPill, STATUS_LABELS } from "@/pages/admin/AdminLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "");

export default function AdminBookings() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [tourSlug, setTourSlug] = useState("all");
  const [tours, setTours] = useState([]);
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [exporting, setExporting] = useState(false);

  const params = useCallback(() => {
    const p = {};
    if (status !== "all") p.status = status;
    if (q.trim()) p.q = q.trim();
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (tourSlug !== "all") p.tour_slug = tourSlug;
    return p;
  }, [status, q, dateFrom, dateTo, tourSlug]);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.get("/admin/bookings", { params: params() }).then((r) => setList(r.data)).finally(() => setLoading(false));
  }, [params]);

  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [load]);

  useEffect(() => { adminApi.get("/admin/tours").then((r) => setTours(r.data)).catch(() => {}); }, []);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await adminApi.get("/admin/bookings/export.csv", { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ams-prenotazioni-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export CSV scaricato");
    } catch (e) { toast.error(errMsg(e, "Export non riuscito (permessi insufficienti?)")); } finally { setExporting(false); }
  };

  const open = (b) => { setSel(b); setNote(b.admin_note || ""); };

  const update = async (newStatus, force = false) => {
    setBusy(true);
    try {
      const r = await adminApi.patch(`/admin/bookings/${sel.id}`, { status: newStatus, admin_note: note, force });
      setSel(r.data);
      toast.success(newStatus === "confirmed" ? "Prenotazione confermata · email inviata al cliente" : newStatus === "cancelled" ? "Prenotazione annullata · email inviata al cliente" : "Prenotazione aggiornata");
      load();
    } catch (e) {
      if (e?.response?.status === 409 && newStatus === "confirmed" && !force) {
        setBusy(false);
        if (window.confirm(`Attenzione: posti insufficienti.\n${errMsg(e)}\n\nVuoi confermare comunque (overbooking)?`)) return update(newStatus, true);
        return;
      }
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const saveNote = async () => {
    setBusy(true);
    try {
      const r = await adminApi.patch(`/admin/bookings/${sel.id}`, { admin_note: note });
      setSel(r.data);
      toast.success("Nota salvata");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const resend = async () => {
    try {
      await adminApi.post(`/admin/bookings/${sel.id}/resend`);
      toast.success("Email reinviata");
      setTimeout(async () => {
        const r = await adminApi.get("/admin/bookings", { params: { q: sel.ref } });
        if (r.data[0]) setSel(r.data[0]);
      }, 2500);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async () => {
    try {
      await adminApi.delete(`/admin/bookings/${sel.id}`);
      toast.success("Prenotazione eliminata");
      setConfirmDel(false);
      setSel(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-bookings">
      <PageTitle title="Prenotazioni" sub="Conferma o annulla: il cliente riceve un'email automatica."
        right={<button onClick={exportCsv} disabled={exporting} className="inline-flex items-center gap-2 px-5 py-3 border border-ocean text-ocean text-sm font-medium rounded-xl btn-lift disabled:opacity-50" data-testid="admin-bookings-export"><Download size={16} />{exporting ? "Esporto..." : "Esporta CSV"}</button>} />
      <AdminCard className="p-4 mb-5 grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="relative md:col-span-4">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ocean/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome, email, telefono, riferimento o tour" className="w-full h-11 pl-10 pr-4 rounded-xl border border-sand bg-white text-sm text-ocean focus:outline-none focus:border-sea" data-testid="admin-bookings-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="md:col-span-2 h-11 rounded-xl border-sand bg-white" data-testid="admin-bookings-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`admin-status-option-${k}`}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={tourSlug} onValueChange={setTourSlug}>
          <SelectTrigger className="md:col-span-3 h-11 rounded-xl border-sand bg-white" data-testid="admin-bookings-tour-filter"><SelectValue placeholder="Tutti i tour" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Tutti i tour</SelectItem>
            {tours.map((t) => <SelectItem key={t.slug} value={t.slug}>{t.translations?.it?.title || t.translations?.en?.title}</SelectItem>)}
          </SelectContent>
        </Select>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} aria-label="Dal" className="md:col-span-1 min-w-0 h-11 px-3 rounded-xl border border-sand bg-white text-sm text-ocean focus:outline-none focus:border-sea" data-testid="admin-bookings-date-from" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} aria-label="Al" className="md:col-span-1 min-w-0 h-11 px-3 rounded-xl border border-sand bg-white text-sm text-ocean focus:outline-none focus:border-sea" data-testid="admin-bookings-date-to" />
        <button onClick={() => { setQ(""); setStatus("all"); setTourSlug("all"); setDateFrom(""); setDateTo(""); }} className="md:col-span-1 h-11 px-3 rounded-xl border border-sand text-xs text-ocean/70 hover:text-ocean" data-testid="admin-bookings-reset">Reset</button>
      </AdminCard>

      <AdminCard className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ivory/70 text-[11px] uppercase tracking-[0.15em] text-ocean/55">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Rif.</th>
                <th className="text-left px-5 py-3 font-medium">Cliente</th>
                <th className="text-left px-5 py-3 font-medium">Tour</th>
                <th className="text-left px-5 py-3 font-medium">Data</th>
                <th className="text-right px-5 py-3 font-medium">Ospiti</th>
                <th className="text-right px-5 py-3 font-medium">Totale</th>
                <th className="text-left px-5 py-3 font-medium">Stato</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sand/60">
              {loading && <tr><td colSpan={7} className="px-5 py-10 text-center text-ocean/45">Caricamento...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-ocean/45" data-testid="admin-bookings-empty">Nessuna prenotazione trovata.</td></tr>}
              {!loading && list.map((b) => (
                <tr key={b.id} onClick={() => open(b)} className="hover:bg-sand/30 cursor-pointer" data-testid={`admin-booking-row-${b.ref}`}>
                  <td className="px-5 py-4 font-medium text-ocean whitespace-nowrap">{b.ref}</td>
                  <td className="px-5 py-4"><p className="text-ocean">{b.name}</p><p className="text-xs text-ocean/50">{b.email}</p></td>
                  <td className="px-5 py-4 text-ocean/75 max-w-[240px] truncate">{b.tour_title}</td>
                  <td className="px-5 py-4 whitespace-nowrap text-ocean/75">{b.date}</td>
                  <td className="px-5 py-4 text-right">{b.guests}</td>
                  <td className="px-5 py-4 text-right font-medium text-ocean">{euro(b.total)}</td>
                  <td className="px-5 py-4"><StatusPill status={b.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AdminCard>

      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="w-full sm:max-w-lg bg-white overflow-y-auto" data-testid="admin-booking-sheet">
          {sel && (
            <>
              <SheetHeader>
                <SheetTitle className="font-serif text-2xl text-ocean">{sel.ref}</SheetTitle>
                <SheetDescription>Ricevuta il {fmt(sel.created_at)}</SheetDescription>
              </SheetHeader>
              <div className="mt-4"><StatusPill status={sel.status} /></div>
              <div className="mt-6 bg-ivory rounded-2xl p-5 space-y-2 text-sm">
                {[["Tour", sel.tour_title], ["Data", sel.date], ["Ospiti", sel.guests],
                  ["Orario richiesto", sel.timeslot || "a scelta / da concordare"],
                  ["Lingua del tour", sel.language ? LANG_NAMES[sel.language] || sel.language : "—"],
                  [sel.pricing_mode === "per_group" ? "Prezzo del gruppo" : "Prezzo a persona", sel.unit_cents !== undefined ? centsToEuro(sel.unit_cents) : euro(sel.price_per_person)],
                  ["Totale", sel.total_cents !== undefined ? centsToEuro(sel.total_cents) : euro(sel.total)], ["Lingua sito", (sel.lang || "en").toUpperCase()]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4"><span className="text-ocean/55">{k}</span><span className="text-ocean text-right font-medium">{v}</span></div>
                ))}
                {sel.kind === "package" && (sel.segments || []).length > 0 && (
                  <div className="pt-3 mt-3 border-t border-sand/70 space-y-1.5" data-testid="admin-booking-segments">
                    <p className="text-[11px] tracking-[0.18em] uppercase text-ocean/55">Pacchetto · giorno per giorno</p>
                    {sel.segments.map((sg, i) => (
                      <div key={i} className="flex justify-between gap-4 text-xs"><span className="text-ocean/60">Giorno {i + 1} · {sg.date}</span><span className="text-ocean text-right">{sg.tour_title}</span></div>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-5 space-y-2 text-sm">
                <p className="font-medium text-ocean">{sel.name}</p>
                <a href={`mailto:${sel.email}`} className="flex items-center gap-2 text-ocean/70 hover:text-sea"><Mail size={14} />{sel.email}</a>
                {sel.phone && <a href={`tel:${sel.phone}`} className="flex items-center gap-2 text-ocean/70 hover:text-sea"><Phone size={14} />{sel.phone}</a>}
                {sel.phone && <a href={waLink(sel.phone, `Ciao ${sel.name}, ti scriviamo da AMS Travel per la prenotazione ${sel.ref}.`)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-emerald-700" data-testid="admin-booking-whatsapp"><MessageCircle size={14} />Scrivi su WhatsApp</a>}
                {sel.notes && <p className="mt-3 bg-sand/40 rounded-xl p-3 text-ocean/75"><span className="block text-[11px] uppercase tracking-wider text-ocean/50 mb-1">Note del cliente</span>{sel.notes}</p>}
              </div>
              <div className="mt-6">
                <label className="block text-[11px] tracking-[0.2em] uppercase text-ocean/55 mb-2">Messaggio per il cliente (incluso nell'email)</label>
                <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Es. Punto d'incontro: hall dell'hotel alle 8:00" className="w-full px-4 py-3 rounded-xl border border-sand text-sm focus:outline-none focus:border-sea resize-none" data-testid="admin-booking-note" />
                <button onClick={saveNote} disabled={busy} className="mt-2 text-xs font-medium text-sea" data-testid="admin-booking-save-note">Salva nota</button>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <button disabled={busy || sel.status === "confirmed"} onClick={() => update("confirmed")} className="py-3 rounded-xl bg-emerald-700 text-white text-sm font-medium disabled:opacity-40 btn-lift" data-testid="admin-booking-confirm">Conferma</button>
                <button disabled={busy || sel.status === "cancelled"} onClick={() => update("cancelled")} className="py-3 rounded-xl bg-rose-700 text-white text-sm font-medium disabled:opacity-40 btn-lift" data-testid="admin-booking-cancel">Annulla</button>
                <button disabled={busy || sel.status === "completed"} onClick={() => update("completed")} className="py-3 rounded-xl bg-ocean text-ivory text-sm font-medium disabled:opacity-40 btn-lift" data-testid="admin-booking-complete">Segna completata</button>
                <button disabled={busy || sel.status === "pending"} onClick={() => update("pending")} className="py-3 rounded-xl border border-sand text-ocean text-sm font-medium disabled:opacity-40 btn-lift" data-testid="admin-booking-pending">Rimetti in attesa</button>
              </div>
              <div className="mt-6">
                <p className="text-[11px] tracking-[0.2em] uppercase text-ocean/55 mb-2">Email inviate</p>
                {(sel.email_log || []).length === 0 ? <p className="text-xs text-ocean/45">Nessuna ancora.</p> : (
                  <ul className="space-y-1.5 text-xs" data-testid="admin-booking-email-log">
                    {sel.email_log.map((e, i) => (
                      <li key={i} className="flex justify-between gap-3">
                        <span className="text-ocean/70">{e.kind} → {e.to}</span>
                        <span className={e.status === "sent" ? "text-emerald-700" : "text-rose-700"}>{e.status === "sent" ? "inviata" : "non inviata"} · {fmt(e.at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="mt-8 flex justify-between border-t border-sand pt-5">
                <button onClick={resend} className="inline-flex items-center gap-2 text-sm text-ocean/70 hover:text-ocean" data-testid="admin-booking-resend"><RefreshCw size={14} />Reinvia email</button>
                <button onClick={() => setConfirmDel(true)} className="inline-flex items-center gap-2 text-sm text-rose-700" data-testid="admin-booking-delete"><Trash2 size={14} />Elimina</button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la prenotazione?</AlertDialogTitle>
            <AlertDialogDescription>L'operazione non può essere annullata. Il cliente non riceverà alcuna email.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Indietro</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-700 hover:bg-rose-800" data-testid="admin-delete-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
